import { WorldState, XenditComponents } from "../public-sdk";
import { BffSession } from "../backend-types/session";
import { BffBusiness } from "../backend-types/business";
import { BffCustomer } from "../backend-types/customer";
import { BffChannel, BffChannelUiGroup } from "../backend-types/channel";
import { internal } from "../internal";
import { ComponentChildren, createContext, FunctionComponent } from "preact";
import { useContext } from "preact/hooks";
import { BffDigitalWallets } from "../backend-types/digital-wallets";

// Create contexts
export const SessionContext = createContext<BffSession | null>(null);
SessionContext.displayName = "SessionContext";

export const BusinessContext = createContext<BffBusiness | null>(null);
BusinessContext.displayName = "BusinessContext";

export const CustomerContext = createContext<BffCustomer | null | undefined>(
  undefined,
);
CustomerContext.displayName = "CustomerContext";

export const ChannelsContext = createContext<BffChannel[] | null>(null);
ChannelsContext.displayName = "ChannelsContext";

export const ChannelUiGroupsContext = createContext<BffChannelUiGroup[] | null>(
  null,
);
ChannelUiGroupsContext.displayName = "ChannelUiGroupsContext";

export const DigitalWalletsContext = createContext<BffDigitalWallets | null>(
  null,
);
DigitalWalletsContext.displayName = "DigitalWalletsContext";

export const SdkContext = createContext<XenditComponents | null>(null);
SdkContext.displayName = "SdkContext";

export const CurrentChannelContext = createContext<BffChannel | null>(null);
CurrentChannelContext.displayName = "CurrentChannelContext";

// Custom hooks for consuming contexts
export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error("useSession must be used within a XenditSessionProvider");
  }
  return context;
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === null) {
    throw new Error("useBusiness must be used within a XenditSessionProvider");
  }
  return context;
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomer must be used within a XenditSessionProvider");
  }
  return context;
};

export const useChannels = () => {
  const context = useContext(ChannelsContext);
  if (context === null) {
    throw new Error("useChannels must be used within a XenditSessionProvider");
  }
  return context;
};

export const useChannelUiGroups = () => {
  const context = useContext(ChannelUiGroupsContext);
  if (context === null) {
    throw new Error(
      "useChannelUiGroups must be used within a XenditSessionProvider",
    );
  }
  return context;
};

export const useDigitalWallets = () => {
  const context = useContext(DigitalWalletsContext);
  return context;
};

export const useSdk = () => {
  const context = useContext(SdkContext);
  if (context === null) {
    throw new Error("useSdk must be used within a XenditSessionProvider");
  }
  return context;
};

export const useCurrentChannel = () => {
  return useContext(CurrentChannelContext);
};

interface XenditSessionProviderProps {
  children: ComponentChildren;
  data: WorldState;
  sdk: XenditComponents;
}

export const XenditSessionProvider: FunctionComponent<
  XenditSessionProviderProps
> = ({ children, data, sdk }) => {
  const {
    session,
    business,
    customer,
    channels,
    digitalWallets,
    channelUiGroups,
  } = data;

  const channel = sdk.getCurrentChannel()?.[internal]?.[0] ?? null;

  if (sdk.getSdkStatus() !== "ACTIVE" || session.status !== "ACTIVE") {
    // clear all contents if the sdk is not initialized or crashes, or if the component is still mounted after completion or failure
    return null;
  }

  return (
    <SdkContext.Provider value={sdk}>
      <CurrentChannelContext.Provider value={channel}>
        <SessionContext.Provider value={session}>
          <BusinessContext.Provider value={business}>
            <CustomerContext.Provider value={customer}>
              <ChannelsContext.Provider value={channels}>
                <DigitalWalletsContext.Provider value={digitalWallets}>
                  <ChannelUiGroupsContext.Provider value={channelUiGroups}>
                    {children}
                  </ChannelUiGroupsContext.Provider>
                </DigitalWalletsContext.Provider>
              </ChannelsContext.Provider>
            </CustomerContext.Provider>
          </BusinessContext.Provider>
        </SessionContext.Provider>
      </CurrentChannelContext.Provider>
    </SdkContext.Provider>
  );
};
